/**
 * outcomeValidation.ts — MER Algo Footprints v8.4
 *
 * Deterministic Outcome Validation & Attribution layer.
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — same inputs always produce same outputs
 * 2. No recomputation of engine logic — reads existing snapshot fields only
 * 3. Replay-safe — serializable inputs and outputs
 * 4. Explainable — named rules, reason strings, no opaque scoring
 * 5. Conservative — does NOT claim causal proof; identifies strongest contributors
 *
 * ── Input sources ────────────────────────────────────────────────────────────
 *
 * Primary:  ResearchSnapshot[]  (pre-trade intelligence + decisionStatus)
 *           JournalEntry[]      (post-trade results: rMultiple, outcome)
 *
 * The attribution layer joins these two by matching on symbol + setup direction
 * + approximate timestamp proximity, or directly when journalEntryId is set.
 *
 * ── Output hierarchy ─────────────────────────────────────────────────────────
 *
 * SnapshotEvaluation         — per-snapshot: validation + attribution + calibration
 * SessionAttributionSummary  — aggregate over a symbol's evaluated snapshots
 *
 * ── Calibration semantics ────────────────────────────────────────────────────
 *
 * Calibration assesses whether conviction at signal time was appropriate for the
 * observed outcome quality. It uses conviction bands and outcome quality buckets.
 *
 *   High conviction + worked = well_calibrated (or lucky if marginal)
 *   High conviction + failed = overconfident
 *   Low  conviction + worked = underconfident
 *   Low  conviction + failed = well_calibrated (appropriate caution)
 *
 * ── Attribution semantics ────────────────────────────────────────────────────
 *
 * Attribution identifies the strongest active signal layers present at snapshot
 * capture time. It does NOT claim these caused the outcome — it identifies
 * what the most informative layers were, weighted by their evidence strength.
 *
 * Layers: gamma | liquidity | trap | volatility | structure | thesis | mixed
 */

import type { ResearchSnapshot } from '../journal/services/researchSnapshot';
import type { JournalEntry }      from '../../shared/types/index';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const OUTCOME_VALIDATION_THRESHOLDS = {
  // Conviction bands for calibration
  HIGH_CONVICTION_MIN:     70,   // ≥70% = high conviction at signal time
  LOW_CONVICTION_MAX:      45,   // <45% = low conviction

  // Attribution weight floors — a layer is 'significant' above this
  ATTRIBUTION_SIGNIFICANT_WEIGHT: 0.20,   // 20% minimum to be a named contributor

  // rMultiple thresholds for outcome quality
  STRONG_WIN_R:            1.5,   // rMultiple ≥ 1.5 = strong win
  WEAK_WIN_R:              0.5,   // 0.5 ≤ rMultiple < 1.5 = weak win
  BREAKEVEN_R_BAND:        0.25,  // |rMultiple| < 0.25 = breakeven

  // Trap count threshold — signal was structurally warned
  TRAP_WARNING_COUNT:      1,     // ≥1 active trap = trap warning present

  // Gamma regime indicating strong structural context
  GAMMA_STRONG_REGIMES:    ['Short Gamma / Expansion', 'Long Gamma / Pinning'] as string[],
} as const;

const T = OUTCOME_VALIDATION_THRESHOLDS;

// ── Types ─────────────────────────────────────────────────────────────────────

export type OutcomeQuality = 'strong_win' | 'weak_win' | 'breakeven' | 'weak_loss' | 'strong_loss' | 'unknown';
export type TimingQuality  = 'early' | 'on_time' | 'late' | 'unknown';
export type CalibrationResult = 'well_calibrated' | 'overconfident' | 'underconfident' | 'insufficient_data';
export type PrimaryAttributionLayer = 'gamma' | 'liquidity' | 'trap' | 'volatility' | 'structure' | 'thesis' | 'mixed' | 'unknown';

export interface AttributionWeight {
  layer:       PrimaryAttributionLayer;
  weight:      number;   // 0–1, sums to ≤1 across all layers
  explanation: string;
}

export interface AttributionBreakdown {
  primaryDriver:    PrimaryAttributionLayer;
  weights:          AttributionWeight[];
  /** One-sentence explanation of what drove the primary attribution */
  primaryNote:      string;
  /** Were multiple layers aligned? */
  convergence:      'single_driver' | 'multi_driver' | 'weak_signal';
}

export interface CalibrationAssessment {
  convictionAtSignal:   number;          // 0–100
  convictionBand:       'high' | 'medium' | 'low';
  outcomeQuality:       OutcomeQuality;
  calibrationResult:    CalibrationResult;
  explanation:          string;
}

export interface OutcomeValidationResult {
  snapshotId:     string;
  symbol:         string;
  snapshotTs:     number;
  /** Direction of the thesis/setup at capture time */
  expectedDir:    'long' | 'short' | 'neutral' | 'unknown';
  /** Conviction at capture time */
  conviction:     number;
  /** What happened (from decisionStatus or journalEntry) */
  decisionStatus: string;
  /** Derived outcome quality (from rMultiple or decisionStatus) */
  outcomeQuality: OutcomeQuality;
  /** rMultiple when available */
  rMultiple:      number | null;
  attribution:    AttributionBreakdown;
  calibration:    CalibrationAssessment;
  /** Named rules that drove this evaluation */
  appliedRules:   string[];
  /** Human-readable reasoning (stable order) */
  reasoning:      string[];
}

export interface SessionAttributionSummary {
  symbol:                string;
  evaluationCount:       number;
  /** Count of acted-on snapshots that worked */
  winCount:              number;
  /** Count of acted-on snapshots that failed */
  lossCount:             number;
  /** Count of acted-on snapshots */
  actedOnCount:          number;
  winRate:               number | null;   // 0–1 when actedOnCount > 0
  /** Most common primary attribution driver */
  dominantAttributionLayer: PrimaryAttributionLayer;
  /** Aggregate calibration across all evaluations */
  overallCalibration:    CalibrationResult;
  /** Average conviction across all acted-on snapshots */
  avgConviction:         number | null;
  /** Snapshot with highest conviction that worked */
  bestEvaluation:        OutcomeValidationResult | null;
  /** Snapshot with highest conviction that failed */
  worstEvaluation:       OutcomeValidationResult | null;
  /** Structured lessons from the attribution patterns */
  lessons:               string[];
  /** Stable reasoning trace */
  reasoning:             string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _round2(v: number): number { return Math.round(v * 100) / 100; }

// ── Step 1: Derive outcome quality from available data ────────────────────────

function _deriveOutcomeQuality(
  status:    string,
  rMultiple: number | null,
): OutcomeQuality {
  // rMultiple is most precise when available
  if (rMultiple !== null) {
    if (rMultiple >= T.STRONG_WIN_R)                                    return 'strong_win';
    if (rMultiple >= T.WEAK_WIN_R)                                      return 'weak_win';
    if (Math.abs(rMultiple) < T.BREAKEVEN_R_BAND)                       return 'breakeven';
    if (rMultiple <= -T.STRONG_WIN_R)                                   return 'strong_loss';
    if (rMultiple < -T.WEAK_WIN_R)                                      return 'weak_loss';
    return 'breakeven';
  }
  // Fall back to decision status
  if (status === 'worked')      return 'weak_win';    // unknown strength without R
  if (status === 'failed')      return 'weak_loss';
  if (status === 'invalidated') return 'unknown';
  if (status === 'passed')      return 'unknown';
  return 'unknown';
}

// ── Step 2: Attribution breakdown ────────────────────────────────────────────

function _buildAttribution(snap: ResearchSnapshot): AttributionBreakdown {
  const weights: AttributionWeight[] = [];
  const rules: string[] = [];

  // ATTR_GAMMA: strong gamma regime present
  const hasStrongGamma = snap.evidence.gammaRegime != null &&
    T.GAMMA_STRONG_REGIMES.includes(snap.evidence.gammaRegime);
  const gammaWeight = hasStrongGamma ? 0.35 : (snap.evidence.gammaRegime ? 0.15 : 0);
  if (gammaWeight > 0) {
    weights.push({ layer: 'gamma', weight: gammaWeight, explanation: `Gamma regime: ${snap.evidence.gammaRegime}` });
    rules.push('ATTR_GAMMA');
  }

  // ATTR_TRAP: active trap signals present at capture time
  const hasTrap = snap.evidence.trapCount >= T.TRAP_WARNING_COUNT;
  const trapWeight = hasTrap ? (snap.risk?.trapConflict ? 0.30 : 0.20) : 0;
  if (trapWeight > 0) {
    weights.push({ layer: 'trap', weight: trapWeight, explanation: `${snap.evidence.trapCount} active trap(s); conflict=${snap.risk?.trapConflict ?? false}` });
    rules.push('ATTR_TRAP');
  }

  // ATTR_STRUCTURE: strong SMC bias
  const hasStrongSMC = snap.marketState.smcBias !== 'neutral' && snap.marketState.biasStrength === 'major';
  const structureWeight = hasStrongSMC ? 0.25 : (snap.marketState.smcBias !== 'neutral' ? 0.10 : 0);
  if (structureWeight > 0) {
    weights.push({ layer: 'structure', weight: structureWeight, explanation: `SMC: ${snap.marketState.smcBias} (${snap.marketState.biasStrength})` });
    rules.push('ATTR_STRUCTURE');
  }

  // ATTR_THESIS: high conviction thesis present
  const convWeight = snap.thesis ? Math.min(0.25, (snap.thesis.conviction / 100) * 0.25) : 0;
  if (convWeight >= T.ATTRIBUTION_SIGNIFICANT_WEIGHT) {
    weights.push({ layer: 'thesis', weight: _round2(convWeight), explanation: `Thesis conviction: ${snap.thesis?.conviction ?? 0}%` });
    rules.push('ATTR_THESIS');
  }

  // ATTR_VOLATILITY: non-normal volatility regime
  // Note: volatility regime not yet in ResearchSnapshot — placeholder for future extension
  // When volatilityRegime is added to ResearchSnapshot, it slots in here

  // Normalise weights so they sum to 1 (if any present)
  const total = weights.reduce((s, w) => s + w.weight, 0);
  if (total > 0) {
    for (const w of weights) w.weight = _round2(w.weight / total);
  }

  // Determine primary driver
  if (weights.length === 0) {
    return {
      primaryDriver: 'unknown',
      weights: [],
      primaryNote: 'Insufficient structured signal data at capture time',
      convergence: 'weak_signal',
    };
  }

  const sortedWeights = [...weights].sort((a, b) => b.weight - a.weight);
  const primary = sortedWeights[0];
  const significant = sortedWeights.filter(w => w.weight >= T.ATTRIBUTION_SIGNIFICANT_WEIGHT);
  const convergence = significant.length >= 3 ? 'multi_driver'
    : significant.length === 1 ? 'single_driver'
    : 'multi_driver';

  const primaryNote = significant.length > 1
    ? `Multi-driver convergence: ${significant.map(w => `${w.layer}(${(w.weight * 100).toFixed(0)}%)`).join(', ')}`
    : `Primary driver: ${primary.layer} — ${primary.explanation}`;

  return {
    primaryDriver: primary.layer,
    weights: sortedWeights,
    primaryNote,
    convergence,
  };
}

// ── Step 3: Calibration assessment ───────────────────────────────────────────

function _buildCalibration(
  conviction:     number,
  outcomeQuality: OutcomeQuality,
): CalibrationAssessment {
  const convictionBand: 'high' | 'medium' | 'low' =
    conviction >= T.HIGH_CONVICTION_MIN ? 'high'
    : conviction < T.LOW_CONVICTION_MAX ? 'low'
    : 'medium';

  if (outcomeQuality === 'unknown') {
    return {
      convictionAtSignal: conviction,
      convictionBand,
      outcomeQuality,
      calibrationResult: 'insufficient_data',
      explanation: 'No outcome data available to assess calibration',
    };
  }

  const isWin  = outcomeQuality === 'strong_win' || outcomeQuality === 'weak_win';
  const isLoss = outcomeQuality === 'weak_loss'  || outcomeQuality === 'strong_loss';

  let result: CalibrationResult;
  let explanation: string;

  if (convictionBand === 'high' && outcomeQuality === 'strong_win') {
    result = 'well_calibrated';
    explanation = `High conviction (${conviction}%) aligned with strong win — calibration appropriate`;
  } else if (convictionBand === 'high' && isLoss) {
    result = 'overconfident';
    explanation = `High conviction (${conviction}%) but ${outcomeQuality} — conviction exceeded actual edge`;
  } else if (convictionBand === 'low' && isWin) {
    result = 'underconfident';
    explanation = `Low conviction (${conviction}%) but ${outcomeQuality} — edge was stronger than assessed`;
  } else if (convictionBand === 'low' && isLoss) {
    result = 'well_calibrated';
    explanation = `Low conviction (${conviction}%) and ${outcomeQuality} — appropriate caution reflected outcome`;
  } else if (convictionBand === 'medium') {
    result = isWin ? 'well_calibrated' : 'overconfident';
    explanation = `Medium conviction (${conviction}%) with ${outcomeQuality}`;
  } else {
    result = 'well_calibrated';
    explanation = `Conviction (${conviction}%) and ${outcomeQuality} — broadly aligned`;
  }

  return { convictionAtSignal: conviction, convictionBand, outcomeQuality, calibrationResult: result, explanation };
}

// ── Step 4: Per-snapshot evaluation ──────────────────────────────────────────

/**
 * Evaluate a single ResearchSnapshot against an optional linked JournalEntry.
 * Journal entry is matched externally (by journalEntryId or caller).
 */
export function evaluateSnapshot(
  snap:         ResearchSnapshot,
  journalEntry: JournalEntry | null = null,
): OutcomeValidationResult {
  const conviction  = snap.thesis?.conviction ?? 0;
  const expectedDir = snap.setup?.dir === 'long' ? 'long'
    : snap.setup?.dir === 'short' ? 'short'
    : 'unknown' as const;

  const rMultiple   = journalEntry?.rMultiple ?? null;
  const outcomeQuality = _deriveOutcomeQuality(snap.decisionStatus, rMultiple);
  const attribution    = _buildAttribution(snap);
  const calibration    = _buildCalibration(conviction, outcomeQuality);

  // Applied rules
  const appliedRules: string[] = [
    `OUTCOME_${snap.decisionStatus.toUpperCase()}`,
    `DIR_${expectedDir.toUpperCase()}`,
    `CONV_BAND_${calibration.convictionBand.toUpperCase()}`,
    ...attribution.weights.map(w => `ATTR_${w.layer.toUpperCase()}`),
  ];

  // Reasoning (stable order)
  const reasoning: string[] = [
    `Setup: ${expectedDir.toUpperCase()} | conviction: ${conviction}% | status: ${snap.decisionStatus}`,
    `Outcome quality: ${outcomeQuality}${rMultiple !== null ? ` (${rMultiple}R)` : ''}`,
    `Attribution: ${attribution.primaryNote}`,
    `Calibration: ${calibration.explanation}`,
    `Regime: ${snap.marketState.regimeDominant} | Gamma: ${snap.evidence.gammaRegime ?? '—'} | Traps: ${snap.evidence.trapCount}`,
  ];

  return {
    snapshotId:     snap.id,
    symbol:         snap.symbol,
    snapshotTs:     snap.createdAt,
    expectedDir,
    conviction,
    decisionStatus: snap.decisionStatus,
    outcomeQuality,
    rMultiple,
    attribution,
    calibration,
    appliedRules,
    reasoning,
  };
}

// ── Step 5: Session attribution summary ──────────────────────────────────────

/**
 * Aggregate all snapshot evaluations for a symbol into a session-level summary.
 * PURE FUNCTION — deterministic from inputs.
 */
export function buildSessionAttributionSummary(
  symbol:      string,
  evaluations: OutcomeValidationResult[],
): SessionAttributionSummary {
  if (evaluations.length === 0) {
    return {
      symbol, evaluationCount: 0, winCount: 0, lossCount: 0, actedOnCount: 0,
      winRate: null, dominantAttributionLayer: 'unknown', overallCalibration: 'insufficient_data',
      avgConviction: null, bestEvaluation: null, worstEvaluation: null,
      lessons: ['No evaluated snapshots available'], reasoning: ['No evaluations to summarize'],
    };
  }

  const actedOn  = evaluations.filter(e => e.decisionStatus === 'worked' || e.decisionStatus === 'failed' || e.decisionStatus === 'acted_on');
  const wins     = actedOn.filter(e => e.outcomeQuality === 'strong_win' || e.outcomeQuality === 'weak_win');
  const losses   = actedOn.filter(e => e.outcomeQuality === 'weak_loss'  || e.outcomeQuality === 'strong_loss');
  const winRate  = actedOn.length > 0 ? _round2(wins.length / actedOn.length) : null;

  // Dominant attribution layer — mode of primaryDriver across all evaluations
  const driverCounts: Record<string, number> = {};
  for (const e of evaluations) {
    driverCounts[e.attribution.primaryDriver] = (driverCounts[e.attribution.primaryDriver] ?? 0) + 1;
  }
  const dominantAttributionLayer = (Object.entries(driverCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'unknown') as PrimaryAttributionLayer;

  // Overall calibration — most common calibration result
  const calibCounts: Record<string, number> = {};
  for (const e of evaluations) {
    calibCounts[e.calibration.calibrationResult] = (calibCounts[e.calibration.calibrationResult] ?? 0) + 1;
  }
  const overallCalibration = (Object.entries(calibCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'insufficient_data') as CalibrationResult;

  // Average conviction (acted-on only)
  const avgConviction = actedOn.length > 0
    ? _round2(actedOn.reduce((s, e) => s + e.conviction, 0) / actedOn.length)
    : null;

  // Best and worst evaluations (acted-on only, by conviction × outcome)
  const bestEvaluation  = wins.length  > 0 ? wins.reduce((b, e)  => e.conviction > b.conviction ? e : b) : null;
  const worstEvaluation = losses.length > 0 ? losses.reduce((w, e) => e.conviction > w.conviction ? e : w) : null;

  // Lessons — from structured pattern analysis
  const lessons: string[] = [];
  const overconfidentCount = evaluations.filter(e => e.calibration.calibrationResult === 'overconfident').length;
  const underconfidentCount = evaluations.filter(e => e.calibration.calibrationResult === 'underconfident').length;
  const trapWarnings = evaluations.filter(e => e.attribution.weights.some(w => w.layer === 'trap')).length;
  const multiDriverWins = wins.filter(e => e.attribution.convergence === 'multi_driver').length;

  if (winRate !== null) {
    lessons.push(`Win rate: ${(winRate * 100).toFixed(0)}% on ${actedOn.length} acted-on snapshot${actedOn.length > 1 ? 's' : ''}`);
  }
  if (overconfidentCount > 0) {
    lessons.push(`${overconfidentCount} overconfident signal${overconfidentCount > 1 ? 's' : ''} — review entries where conviction exceeded actual edge`);
  }
  if (underconfidentCount > 0) {
    lessons.push(`${underconfidentCount} underconfident signal${underconfidentCount > 1 ? 's' : ''} — structural edge was stronger than conviction reflected`);
  }
  if (trapWarnings > 0) {
    const trapWins = wins.filter(e => e.attribution.weights.some(w => w.layer === 'trap')).length;
    lessons.push(`${trapWarnings} snapshot${trapWarnings > 1 ? 's' : ''} with trap context${trapWins > 0 ? ` — ${trapWins} resolved favorably` : ' — review these entries'}`);
  }
  if (multiDriverWins > 0) {
    lessons.push(`${multiDriverWins} win${multiDriverWins > 1 ? 's' : ''} driven by multi-layer convergence — these are the highest-quality setup conditions`);
  }
  if (dominantAttributionLayer !== 'unknown') {
    lessons.push(`Dominant signal layer: ${dominantAttributionLayer} — review whether this layer is being consistently identified pre-trade`);
  }
  if (lessons.length === 0) lessons.push('Insufficient data to derive structured lessons');

  // Reasoning
  const reasoning: string[] = [
    `Session: ${evaluations.length} evaluations | acted-on: ${actedOn.length} | win rate: ${winRate !== null ? (winRate * 100).toFixed(0) + '%' : 'N/A'}`,
    `Dominant attribution: ${dominantAttributionLayer} | overall calibration: ${overallCalibration}`,
    `Avg conviction on acted-on trades: ${avgConviction !== null ? avgConviction + '%' : 'N/A'}`,
  ];
  if (bestEvaluation)  reasoning.push(`Best: ${bestEvaluation.symbol} ${bestEvaluation.expectedDir} @ ${bestEvaluation.conviction}% conviction — ${bestEvaluation.outcomeQuality}`);
  if (worstEvaluation) reasoning.push(`Worst: ${worstEvaluation.symbol} ${worstEvaluation.expectedDir} @ ${worstEvaluation.conviction}% conviction — ${worstEvaluation.outcomeQuality}`);

  return {
    symbol, evaluationCount: evaluations.length,
    winCount: wins.length, lossCount: losses.length, actedOnCount: actedOn.length,
    winRate, dominantAttributionLayer, overallCalibration, avgConviction,
    bestEvaluation, worstEvaluation, lessons, reasoning,
  };
}

// ── Main entry point ──────────────────────────────────────────────────────────

/**
 * Evaluate all ResearchSnapshots for a symbol against available journal entries.
 * PURE FUNCTION — deterministic from inputs.
 *
 * @param symbol       — symbol being evaluated
 * @param snapshots    — ResearchSnapshot[] for this symbol
 * @param journalEntries — JournalEntry[] for context (matched by journalEntryId)
 */
export function evaluateSession(
  symbol:         string,
  snapshots:      ResearchSnapshot[],
  journalEntries: JournalEntry[],
): { evaluations: OutcomeValidationResult[]; summary: SessionAttributionSummary } {
  const symbolSnaps = snapshots.filter(s => s.symbol === symbol);

  // Build a map from journalEntryId → JournalEntry for O(1) lookup
  const journalMap = new Map<number, JournalEntry>();
  for (const je of journalEntries) journalMap.set(je.id, je);

  const evaluations: OutcomeValidationResult[] = symbolSnaps.map(snap => {
    const linkedEntry = snap.journalEntryId != null ? journalMap.get(snap.journalEntryId) ?? null : null;
    return evaluateSnapshot(snap, linkedEntry);
  });

  // Sort by snapshot timestamp — deterministic ordering
  evaluations.sort((a, b) => a.snapshotTs - b.snapshotTs);

  const summary = buildSessionAttributionSummary(symbol, evaluations);
  return { evaluations, summary };
}
